import type {
  CreatePackageOrderInputDto,
  CreateOrderInputDto,
} from "@/types/codegen";

export enum PurchaseStatus {
  SUCCESSFUL = "SUCCESSFUL",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
  EXPIRED = "EXPIRED",
}

export type PaymentResult = {
  orderId: string;
  status: PurchaseStatus;
};

export type PaymentMethod = "qiAccount" | "checkout" | "bnpl";
export type PaymentProviderType = "standard" | "bnpl";

type PaymentProviderBase<TParams> = {
  initiatePayment: (params: TParams) => Promise<PaymentResult>;
};

export type TripPaymentParams = Omit<CreatePackageOrderInputDto, "provider">;
export type FlightPaymentParams = Omit<CreateOrderInputDto, "provider">;
export type HotelPaymentParams = Omit<CreateOrderInputDto, "provider">;

type SuccessCallbackData = {
  orderId: string;
};

type FailureCallbackData = {
  orderId: string | undefined;
  errorType: string;
  errorMessage: string;
};

export type Callbacks = {
  onSuccess: (data: SuccessCallbackData) => void;
  onFailure: (data: FailureCallbackData) => void;
};

type PaymentParams<TService extends string, TPayload> = {
  serviceType: TService;
} & Callbacks &
  TPayload;

export type StandardTripPaymentParams = PaymentParams<
  "trip",
  TripPaymentParams
>;

export type StandardFlightPaymentParams = PaymentParams<
  "air",
  FlightPaymentParams
>;

export type StandardHotelPaymentParams = PaymentParams<
  "hotel",
  HotelPaymentParams
>;

export type StandardInitiatePaymentParams =
  | StandardTripPaymentParams
  | StandardFlightPaymentParams
  | StandardHotelPaymentParams;

export const isTripPayment = (
  params: StandardInitiatePaymentParams,
): params is StandardTripPaymentParams => {
  return params.serviceType === "trip";
};

export type StandardPaymentProvider =
  PaymentProviderBase<StandardInitiatePaymentParams>;

export type BnplPaymentProvider = PaymentProviderBase<{
  availabilityTokens: string[];
  sessionId: string;
  otp: string;
  promoCode?: string;
}>;

export type PaymentProvider = StandardPaymentProvider | BnplPaymentProvider;
